// TEN18 (docs/build-briefs/TEN18-publish-discounts-adopt-by-receipt.md; ADR-008, DEC-339, DEC-342 Q6, P0.21): the pure
// decisions behind the location's "From <network name>" area in Settings > Discounts, and behind how an adopted copy
// reads in the discount list. No DOM, no fetch: test/ten18-publications-ui-model.test.ts proves it.
//
// The area lists the OPTIONAL discounts the franchise network offers this location, A to Z (DEC-339), each with its terms
// in words, the locations it applies at, and Accept and Decline (TEN18-F1, DEC-345: "Adopt" is renamed "Accept"). Accepting
// creates the location's own discount (the list below then shows it with a "From <network name>" chip and its terms
// read-only); declining only records the answer. A declined offer can still be accepted. A withdrawn publication is
// not offered at all. A REQUIRED discount is never an offer: it arrives as a locked row in the list.

import { sortByName } from "./nameSort.ts";
import { isDiscountTermsShape, termsSummary, type DiscountTerms } from "./publishedDiscountTerms.ts";
import {
  compositionLabel,
  isCompositionDto,
  isIsoDate,
  windowText,
  type CompositionDto,
} from "./publishedWindowModel.ts";

export type OfferStateDto = "pending" | "adopted" | "declined";

export interface OfferDto extends DiscountTerms {
  publicationId: string;
  name: string;
  state: OfferStateDto;
  decidedAt: string | null;
  discountId: string | null;
  /** The locations of this franchisee the offer applies at; empty means every location. */
  locations: { id: string; name: string }[];
  /** TEN18-F5. The network's central window and stacking declaration, read-only. */
  windowStartsOn: string | null;
  windowEndsOn: string | null;
  membershipComposition: CompositionDto;
}

export interface OffersResponseDto {
  network: { name: string } | null;
  offers: OfferDto[];
}

export function isOffersShape(value: unknown): value is OffersResponseDto {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const network = v["network"];
  if (network !== null) {
    if (typeof network !== "object" || typeof (network as Record<string, unknown>)["name"] !== "string") return false;
  }
  const offers = v["offers"];
  if (!Array.isArray(offers)) return false;
  return offers.every((row) => {
    if (!isDiscountTermsShape(row)) return false;
    const r = row as unknown as Record<string, unknown>;
    return (
      typeof r["publicationId"] === "string" &&
      typeof r["name"] === "string" &&
      (r["state"] === "pending" || r["state"] === "adopted" || r["state"] === "declined") &&
      (r["decidedAt"] === null || typeof r["decidedAt"] === "string") &&
      (r["discountId"] === null || typeof r["discountId"] === "string") &&
      (r["windowStartsOn"] === null || isIsoDate(r["windowStartsOn"])) &&
      (r["windowEndsOn"] === null || isIsoDate(r["windowEndsOn"])) &&
      isCompositionDto(r["membershipComposition"]) &&
      Array.isArray(r["locations"]) &&
      (r["locations"] as unknown[]).every((b) => {
        if (typeof b !== "object" || b === null) return false;
        const siteLocation = b as Record<string, unknown>;
        return typeof siteLocation["id"] === "string" && typeof siteLocation["name"] === "string";
      })
    );
  });
}

export function offersHeading(networkName: string): string {
  return `From ${networkName}`;
}

export const OFFERS_LOAD_FAILED = "The network's discounts could not be loaded. Check your connection and reopen this screen.";

export interface StateView {
  label: string;
  /** Which existing pill colour: neutral, good or warn. No new colours. */
  tone: "neutral" | "good" | "warn";
}

export function offerStateView(state: OfferStateDto): StateView {
  switch (state) {
    case "pending":
      return { label: "Not answered", tone: "neutral" };
    case "adopted":
      return { label: "Accepted", tone: "good" };
    case "declined":
      return { label: "Declined", tone: "warn" };
  }
}

export interface OfferView {
  publicationId: string;
  name: string;
  termsText: string;
  /** "Location A, Location B", or "All locations" for an offer that names none. */
  locationsText: string;
  /** TEN18-F5. "N/A" when the window is open. */
  windowText: string;
  stackingText: string;
  stateLabel: string;
  tone: StateView["tone"];
  canAccept: boolean;
  canDecline: boolean;
}

export function locationsText(names: readonly string[]): string {
  if (names.length === 0) return "All locations";
  return sortByName(names, (n) => n).join(", ");
}

/** The offers A to Z (DEC-339) whatever order the server sent; the input is not mutated. */
export function offerViews(offers: readonly OfferDto[]): OfferView[] {
  return sortByName(offers, (o) => o.name).map((o) => {
    const state = offerStateView(o.state);
    return {
      publicationId: o.publicationId,
      name: o.name,
      termsText: termsSummary(o),
      locationsText: locationsText(o.locations.map((b) => b.name)),
      windowText: windowText(o.windowStartsOn, o.windowEndsOn),
      stackingText: compositionLabel(o.membershipComposition),
      stateLabel: state.label,
      tone: state.tone,
      canAccept: o.state !== "adopted",
      canDecline: o.state === "pending",
    };
  });
}

/** Whether the area shows at all: only a location with something offered sees it. */
export function showsOffers(response: OffersResponseDto | null): boolean {
  return response !== null && response.network !== null && response.offers.length > 0;
}

export function adoptedMessage(name: string): string {
  return `${name} accepted.`;
}

export function declinedMessage(name: string): string {
  return `${name} declined.`;
}

export const OFFER_FAILED_MESSAGE = "That did not go through. Nothing was changed. Try again.";

/** Words for a failed adopt or decline. A name clash and a withdrawal carry the server's own plain sentence. */
export function describeOfferFailure(status: number, body: { error?: string; message?: string } | null): string {
  if (status === 0) return "Could not reach the server. Check your connection and try again.";
  if (status === 403) return "Your role does not allow changing discounts.";
  if (status === 404) return "That discount is no longer offered to you. Reopen this screen.";
  if (
    body !== null &&
    typeof body.message === "string" &&
    body.message !== "" &&
    (body.error === "duplicate_active_name" ||
      body.error === "publication_withdrawn" ||
      body.error === "publication_already_adopted" ||
      body.error === "publication_required")
  ) {
    return body.message.charAt(0).toUpperCase() + body.message.slice(1);
  }
  return OFFER_FAILED_MESSAGE;
}

// ---- An adopted copy in the discount list --------------------------------------------------------------------

/** The parts of a discount row the copy view needs (the settings DiscountDto satisfies it). */
export interface CopyRowInput {
  name: string;
  markdown_type: "percentage" | "amount";
  markdown_basis_points: number | null;
  markdown_amount_cents: number | null;
  applies_to_tickets: boolean;
  applies_to_items: boolean;
  requires_approval: boolean;
  requires_reason: boolean;
  network_publication_id?: string | null;
  networkSource?: {
    networkName: string;
    withdrawn: boolean;
    required?: boolean;
    locationNames?: readonly string[];
    windowStartsOn?: string | null;
    windowEndsOn?: string | null;
    membershipComposition?: CompositionDto;
  } | null;
}

export function isNetworkCopy(row: Pick<CopyRowInput, "network_publication_id">): boolean {
  return typeof row.network_publication_id === "string" && row.network_publication_id !== "";
}

export interface CopyRowView {
  chipText: string;
  termsText: string;
  withdrawn: boolean;
  /** "Required" for a copy the network requires, else empty. A required copy has no switch. */
  requiredLabel: string;
  /** The locations the copy applies at ("Location A, Location B"), or empty for a copy that applies at every location. */
  locationsText: string;
  /** Short words under the terms; empty when nothing needs saying. */
  note: string;
  /** TEN18-F5. The network's window in words ("N/A" when open) and its stacking declaration, shown read-only. */
  windowText: string;
  stackingText: string;
  /** Whether the row has an On and Off switch at all: not for a required copy. */
  hasSwitch: boolean;
  /** The switch is disabled once the network withdrew the discount. */
  canSwitch: boolean;
}

export function copyRowView(row: CopyRowInput): CopyRowView {
  const network = row.networkSource?.networkName ?? "your franchise network";
  const withdrawn = row.networkSource?.withdrawn === true;
  const required = row.networkSource?.required === true;
  const locations = row.networkSource?.locationNames ?? [];
  return {
    chipText: `From ${network}`,
    termsText: termsSummary({
      markdownType: row.markdown_type,
      markdownBasisPoints: row.markdown_basis_points,
      markdownAmountCents: row.markdown_amount_cents,
      appliesToTickets: row.applies_to_tickets,
      appliesToItems: row.applies_to_items,
      requiresApproval: row.requires_approval,
      requiresReason: row.requires_reason,
    }),
    withdrawn,
    requiredLabel: required ? "Required" : "",
    locationsText: locations.length === 0 ? "" : locationsText(locations),
    windowText: windowText(row.networkSource?.windowStartsOn ?? null, row.networkSource?.windowEndsOn ?? null),
    stackingText: compositionLabel(row.networkSource?.membershipComposition ?? "sequential"),
    note: withdrawn ? `Withdrawn by ${network}` : "",
    hasSwitch: !required,
    canSwitch: !withdrawn,
  };
}
